import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiBaseUrl } from '../../util/api';
import ImageCropModal from '../ImageCropModal/ImageCropModal';
import styles from './ImageUpload.module.css';

/**
 * Reusable ImageUpload component for uploading images to S3
 * Following DRY principle - single component for all image upload needs
 *
 * Features:
 * - File selection with drag & drop support
 * - Image cropping with configurable aspect ratio
 * - Image preview
 * - Direct upload to S3 via backend
 * - Display existing image in edit mode
 * - File size validation (configurable, default 20MB)
 * - Image type validation (all image types)
 * - Upload progress indication
 */
const ImageUpload = ({
  onUploadComplete,
  onUploadError,
  onUploadingChange,
  existingImageUrl = null,
  maxSizeInMB = 20,
  label = 'Upload Image',
  isOptional = true,
  helpText = 'Recommended: 400x250px, JPG or PNG',
  enableCrop = true,
  aspectRatio = 1,
  circularCrop = false,
  cropTitle = 'Crop Your Image',
  cropInstructions = 'Drag to adjust the crop area to fit your image perfectly.',
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(existingImageUrl);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // Update preview when existing image changes
  useEffect(() => {
    if (existingImageUrl) {
      setPreviewUrl(existingImageUrl);
    }
  }, [existingImageUrl]);

  /**
   * Validate file before upload
   */
  const validateFile = file => {
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      return 'Please select an image file';
    }

    // Check file size (convert MB to bytes)
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return `File size must be less than ${maxSizeInMB}MB`;
    }

    return null;
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async file => {
    if (!file) return;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      if (onUploadError) {
        onUploadError(new Error(validationError));
      }
      return;
    }

    setError(null);

    // If crop is enabled, show crop modal
    if (enableCrop) {
      setPendingFile(file);
      setShowCropModal(true);
    } else {
      // Direct upload without cropping
      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to S3
      await uploadToS3(file);
    }
  };

  /**
   * Handle crop modal close
   */
  const handleCropModalClose = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  /**
   * Handle crop save - upload cropped image
   */
  const handleCropSave = async (croppedImageBlob) => {
    setShowCropModal(false);
    
    // Convert blob to file
    const croppedFile = new File([croppedImageBlob], pendingFile.name, {
      type: croppedImageBlob.type || 'image/jpeg',
      lastModified: Date.now(),
    });

    setSelectedFile(croppedFile);

    // Create preview from blob
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(croppedImageBlob);

    // Upload cropped image to S3
    await uploadToS3(croppedFile);
    setPendingFile(null);
  };

  /**
   * Upload file to S3 via backend. Genuinely promise-based (resolves/rejects
   * from the xhr's own load/error events) so callers that `await` this --
   * e.g. handleCropSave -- actually wait for the upload to finish instead of
   * the promise resolving as soon as xhr.send() is called (which used to let
   * a parent's own Save/Submit button fire before onUploadComplete ever ran,
   * silently dropping the image).
   */
  const uploadToS3 = file => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    onUploadingChange?.(true);

    return new Promise(resolve => {
      const formData = new FormData();
      formData.append('image', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        setUploading(false);
        onUploadingChange?.(false);

        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          setUploadProgress(100);

          if (onUploadComplete) {
            onUploadComplete({
              url: response.url,
              key: response.key,
            });
          }
        } else {
          setError('Failed to upload image. Please try again.');
          setUploadProgress(0);
          if (onUploadError) {
            onUploadError(new Error('Upload failed'));
          }
        }
        resolve();
      });

      xhr.addEventListener('error', () => {
        console.error('Error uploading file');
        setError('Failed to upload image. Please try again.');
        setUploading(false);
        setUploadProgress(0);
        onUploadingChange?.(false);
        if (onUploadError) {
          onUploadError(new Error('Upload failed'));
        }
        resolve();
      });

      xhr.open('POST', `${apiBaseUrl()}/api/upload-image`);
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  };

  /**
   * Handle file input change
   */
  const handleInputChange = e => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Clear selected image
   */
  const handleClear = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    setUploadProgress(0);

    // Notify parent about clearing (set to null)
    if (onUploadComplete) {
      onUploadComplete(null);
    }
  };

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        {label} {isOptional && <span className={styles.optional}>(optional)</span>}
      </label>

      {previewUrl ? (
        // Image Preview
        <div className={styles.previewContainer}>
          <div className={styles.imageWrapper}>
            <img src={previewUrl} alt="Preview" className={styles.previewImage} />
            {!uploading && (
              <button
                type="button"
                onClick={handleClear}
                className={styles.clearButton}
                title="Remove image"
              >
                ✕
              </button>
            )}
            {uploading && (
              <div className={styles.uploadOverlay}>
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className={styles.uploadText}>Uploading... {uploadProgress}%</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        // Upload Area
        <div
          className={`${styles.uploadArea} ${isDragging ? styles.dragging : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className={styles.uploadPrompt}>
            <svg
              className={styles.uploadIcon}
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p className={styles.uploadText}>
              {isDragging ? 'Drop image here' : 'Drag & drop or click to upload'}
            </p>
            <p className={styles.uploadSubtext}>All image types • Max {maxSizeInMB}MB</p>
          </div>

          {/* Hidden File Input */}
          <input
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className={styles.fileInput}
            disabled={uploading}
          />
        </div>
      )}

      {/* Help Text */}
      {helpText && <p className={styles.helpText}>{helpText}</p>}

      {/* Error Message */}
      {error && <p className={styles.error}>{error}</p>}

      {/* Crop Modal */}
      {enableCrop && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropModalClose}
          onSave={handleCropSave}
          imageFile={pendingFile}
          aspectRatio={aspectRatio}
          circularCrop={circularCrop}
          title={cropTitle}
          instructions={cropInstructions}
        />
      )}
    </div>
  );
};

ImageUpload.propTypes = {
  onUploadComplete: PropTypes.func.isRequired,
  onUploadError: PropTypes.func,
  onUploadingChange: PropTypes.func,
  existingImageUrl: PropTypes.string,
  maxSizeInMB: PropTypes.number,
  label: PropTypes.string,
  isOptional: PropTypes.bool,
  helpText: PropTypes.string,
  enableCrop: PropTypes.bool,
  aspectRatio: PropTypes.number,
  circularCrop: PropTypes.bool,
  cropTitle: PropTypes.string,
  cropInstructions: PropTypes.string,
};

export default ImageUpload;
